import { useRef, useEffect, useMemo, useState } from 'react';
import { NextSeo, ProductJsonLd, FAQPageJsonLd } from 'next-seo';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import OtherOptions from 'src/components/OtherOptions';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import RevalidateSettings from 'src/lib/revalidate_settings';
import { Image, Category, Collection, SimpleCollection, TravelItinerary, Pagination, Blog } from 'src/types';
import ApiClient from 'src/lib/api_client';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import Link from 'next/link';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import useFilter from 'src/hooks/useFilter';
import { DefaultCollectionHeroImage, HEADER_HEIGHT } from 'src/lib/constants';
import CollectionHorizontalCard from 'src/components/CollectionHorizontalCard';
import useBreakpoints from 'src/hooks/useBreakpoints';
import Slider from 'react-slick';
import { commonCarouselSettings } from 'src/lib/constants';
import ImageWithFallback from 'src/components/ImageWithFallback';
import imageStyle from 'src/styles/modules/components/image.module.scss';
import Homework from 'src/components/Homework';
import { scrollTo } from 'src/helpers';
import { sortBy, truncate } from 'lodash-es';
import useInitialData from 'src/hooks/useInitialData';
import useFetchCountryOrCityCollections from 'src/hooks/useFetchCountryOrCityCollections';
import CountryCityPageNav from 'src/components/CountryCityPageNav';
import useQuery from 'src/hooks/useQuery';
import TopCollectionSection from 'src/components/city/TopCollectionSection';
import Banner from 'src/components/Banner';

const metaTitle = (category: Category, collection: Collection) => {
  return `${category.name} for ${collection.name}`;
};

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  category,
  collection,
}) => {
  const { isScreenMd } = useBreakpoints();
  const initialData = useInitialData();
  const containerOptionsRef = useRef<HTMLDivElement>(null)
  const { setFilterRecord } = useFilter();
  const image: Image | undefined = category.images[0] || collection.image || DefaultCollectionHeroImage;
  const images = category.images.length >= 3 ? category.images : category.images.concat(
    collection.images && collection.images.length > 0
      ? collection.images
      : []
  );

  const countryOrCityRecord = collection.cityId
    ? initialData.cities.find((o) => o.id === collection.cityId)
    : collection.countryId
    ? initialData.countries.find((o) => o.id === collection.countryId)
    : undefined;

  const { data: travelItineraryData } = useQuery<{
    data: TravelItinerary[];
    pagination: Pagination;
  }>(
    ApiRoutes.apiTravelItinerariesRoute(
      {},
      {
        q: {
          g: {
            0: {
              status_eq: 'published',
            },
            1: {
              g: {
                0: {
                  country_id_eq: collection.countryId,
                },
                1: {
                  travel_itinerary_cities_city_country_id_eq: collection.countryId,
                },
                2: {
                  travel_itinerary_cities_city_id_in: collection.cityId ? [collection.cityId] : [],
                },
              },
              m: 'or',
            },
          },
          s: 'views_count desc',
        },
        page: 1,
        per_page: 20,
      }
    ).toUrl()
  );

  const { data: blogsData } = useQuery<{ data: Blog[]; pagination: Pagination }>(
    ApiRoutes.apiBlogsRoute(
      {},
      {
        collection_ids: [collection.id],
        sort: 'most_read',
        page: 1,
        per_page: 20,
      }
    ).toUrl()
  );

  const cities = useMemo(
    () =>
      sortBy(
        initialData.cities.filter((o) => o.countryId === collection.countryId),
        [(o) => (o.featured ? 0 : 1), (o) => -o.optionsCount]
      ),
    [collection.id, initialData.cities.length]
  );

  const { data: itineraries = [] } = travelItineraryData || {};
  const { data: blogs = [] } = blogsData || {};
  const { locationCollections, listCollections, activityCollections } =
    useFetchCountryOrCityCollections(countryOrCityRecord, collection.cityId ? 'City' : 'Country');
  const [popupType, setPopupType] = useState< 'collections' | undefined>(undefined);
  const sortedBlogs = sortBy(blogs, (o) =>
    o.linkableType === 'Collection' && Number(o.linkableId) === collection.id ? 0 : 1
  );

  const otherCategories = useMemo(
    () =>
      (collection.categories || [])
        .map((o) =>
          o.id === category.id
            ? null
            : {
                id: o.id,
                url: AppRoutes.cateCollectionRoute({
                  id: collection.slugCode,
                  cateId: o.slug,
                }).toUrl(),
                name: o.name,
                strippedDescription: o.description,
                image: o.image || collection.image || DefaultCollectionHeroImage,
              }
        )
        .filter((o) => o) as SimpleCollection[],
    [category.id]
  );
  const otherRecords = useMemo(() => [collection, ...otherCategories], [category.id]);

  useEffect(() => {
    if (category.id) {
      setFilterRecord({
        id: category.id,
        type: 'Category',
        name: category.name,
        parentId: collection.id,
        parentType: 'Collection',
      });
    }
  }, [category.id]);

  return (
    <>
      <NextSeo
        title={metaTitle(category, collection)}
        description={category.description}
        canonical={AppRoutes.cateCollectionRoute({
          id: collection.slugCode,
          cateId: category.slug,
        }).toUrl()}
        openGraph={{
          title: metaTitle(category, collection),
          description: category.description,
          images: image?.mdUrl
            ? [
                {
                  url: image.mdUrl,
                  width: 600,
                  height: 600,
                  alt: metaTitle(category, collection),
                },
              ]
            : undefined,
        }}
      />
      <WithDefaultJsonLd>
        <ProductJsonLd
          productName={collection.name}
          description={collection.description}
          images={image?.mdUrl ? [image.mdUrl] : []}
          offers={{
            url: AppRoutes.cateCollectionRoute({
              id: collection.slugCode,
              cateId: category.slug,
            }).toUrl(),
            availability: 'https://schema.org/InStock',
          }}
          aggregateRating={{
            ratingValue: collection.reviewRating,
            reviewCount: collection.ratedReviewsCount,
          }}
        />
        {collection.nextSeoFaqs ? <FAQPageJsonLd mainEntity={collection.nextSeoFaqs} /> : null}
      </WithDefaultJsonLd>

      {countryOrCityRecord ? (
        <CountryCityPageNav
          record={countryOrCityRecord}
          blogs={sortedBlogs}
          collection={collection}
          travelItineraries={itineraries}
          collectionCategories={otherCategories}
          listCollections={listCollections}
          activityCollections={activityCollections}
          locationCollections={locationCollections}
          cities={cities}
          outerPopupType={popupType}
          onClose={() => setPopupType(undefined)}
        />
      ) : null}

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
        <div className="container">
          <Breadcrumb className="breadcrumb--black tw-justify-center tw-mb-4 md:tw-mb-0 tw-pt-0 tw-text-xs md:tw-text-sm">
            {collection.cityId ? (
              <>
                {collection.citySlug !== 'singapore' ? (
                  <BreadcrumbItem className="tw-hidden md:tw-block">
                    <Link legacyBehavior prefetch={false}                       href={AppRoutes.countryRoute({
                        id: collection.countrySlug as string,
                      }).toUrl()}
                    >
                      <a>{collection.countryName}</a>
                    </Link>
                  </BreadcrumbItem>
                ) : null}
                <BreadcrumbItem className="tw-hidden md:tw-block">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.cityRoute({ id: collection.citySlug as string }).toUrl()}>
                    <a>{collection.cityName}</a>
                  </Link>
                </BreadcrumbItem>
              </>
            ) : collection.countryId ? (
              <BreadcrumbItem className="tw-hidden md:tw-block">
                {collection.countrySlug === 'singapore' ? (
                  <a href={AppRoutes.countryRoute({ id: collection.countrySlug }).toPath()}>
                    {collection.countryName}
                  </a>
                ) : (
                  <Link legacyBehavior prefetch={false}                     href={AppRoutes.countryRoute({ id: collection.countrySlug as string }).toUrl()}
                  >
                    <a>{collection.countryName}</a>
                  </Link>
                )}
              </BreadcrumbItem>
            ) : null}
            <BreadcrumbItem>
              <Link legacyBehavior prefetch={false} href={AppRoutes.collectionRoute({ id: collection.slugCode }).toUrl()}>
                <a>{collection.name}</a>
              </Link>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>

        <div className="container tw-max-w-2xl tw-mt-8 tw-mb-8 md:tw-mb-16">
          <h1 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-4xl md:tw-text-6xl">
            {category.name}
          </h1>
        </div>

        <div className="container tw-max-w-8xl tw-px-0 md:tw-px-3.75 tw-relative">
          <Slider
            {...commonCarouselSettings}
            slidesToShow={1}
            slidesToScroll={1}
            centerMode={true}
            centerPadding="400px"
            infinite={true}
            responsive={[
              {
                breakpoint: 1040,
                settings: {
                  centerPadding: '200px',
                  arrows: false,
                },
              },
              {
                breakpoint: 800,
                settings: {
                  centerPadding: typeof window === 'object' ? `${Math.round((window.innerWidth - 296) / 2)}px` : '67px',
                  arrows: false
                },
              },
            ]}
            className="tw--mx-2 md:tw--mx-4"
          >
            {images.map((image, index) => (
              <div key={index} className="tw-px-2 md:tw-px-4 tw-outline-none">
                <ImageWithFallback
                  record={image}
                  imgField="webpLgUrl"
                  alt=""
                  className={`tw-block tw-static ${imageStyle.heroImage}`}
                  fill
                />
              </div>
            ))}
          </Slider>
        </div>

        <div className="tw-mt-16 tw-text-center tw-w-72 tw-px-1 md:tw-max-w-md tw-mx-auto">
          <div>{category.description}</div>
        </div>
      </div>

      <div className="tw-py-16 md:tw-py-24">
        <div ref={containerOptionsRef}></div>
        <OtherOptions
          hideTitle={true}
          isGiftExperience={!!collection.giftCountryId}
          record={{
            id: category.id,
            type: 'Category',
          }}
        />
      </div>

      <div className="tw-bg-purple tw-py-16 md:tw-py-24">
        <div className="container tw-mb-8 md:tw-mb-16">
          <h2 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-4xl">
            More From This Collection
          </h2>
        </div>

        <div className="container">
          <div className="row tw-hidden md:tw-flex">
            {otherRecords.slice(0, 4).map((collection) => (
              <div key={collection.id} className="col-6 tw-mb-8">
                <CollectionHorizontalCard
                  collection={collection}
                  title={collection.name}
                  descLength={100}
                />
              </div>
            ))}
          </div>

          <div className="tw-block md:tw-hidden">
            <Slider
              {...commonCarouselSettings}
              slidesToScroll={1}
              slidesToShow={1}
              arrows={false}
              dots={true}
              className="slider-v2 slider-v2-green-dots carousel-homework tw--mx-2"
            >
              {otherRecords.slice(0, 4).map((collection) => (
                <div key={collection.id} className="tw-mb-8 tw-px-2 tw-outline-none">
                  <Link legacyBehavior prefetch={false} href={'slugCode' in collection ? AppRoutes.collectionRoute({ id: collection.slugCode }).toPath() : collection.url}>
                    <a className="tw-block">
                      <ImageWithFallback
                        record={collection.image}
                        imgField="webpMdUrl"
                        alt=""
                        className="tw-block tw-static tw-h-56 tw-w-full tw-object-cover tw-mb-8 md:tw-mb-6 tw-border-2 tw-border-solid tw-border-brand-green tw-rounded-lg"
                        fill
                      />
                      <div className="tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-xl tw-leading-none tw-mb-5 md:tw-mb-4 tw-text-center">{collection.name}</div>
                      <div className="tw-text-center">{truncate(collection.strippedDescription, { length: 100, omission: '...' })}</div>
                    </a>
                  </Link>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      <Homework />

      {countryOrCityRecord ? (
        <>
          <Banner
            title={`Top things to do in ${countryOrCityRecord.name}`}
            image={countryOrCityRecord.image}
            url={
              collection.cityId
                ? AppRoutes.cityRoute({ id: countryOrCityRecord.slug }).toUrl()
                : AppRoutes.countryRoute({ id: countryOrCityRecord.slug }).toUrl()
            }
            urlLabel={`explore ${countryOrCityRecord.name}`}
          />

          {!collection.giftCountryId &&
          [activityCollections, locationCollections, listCollections, cities].some(
            (o) => o.length > 0
          ) ? (
            <TopCollectionSection
              title={`${countryOrCityRecord.name} Collections`}
              record={{
                type: collection.countryId ? 'Country' : 'City',
                name: countryOrCityRecord.name,
                slug: countryOrCityRecord.slug,
              }}
              listCollections={listCollections}
              activityCollections={activityCollections}
              locationCollections={locationCollections}
              cities={cities}
              limit={isScreenMd ? 15 : undefined}
              onShowMore={(tab) =>
                setPopupType('collections')
              }
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{ category: Category; collection: Collection }> =
  async (context) => {
    const { id, cateId } = context.params || {};

    if (!id || !cateId) {
      return {
        notFound: true,
      };
    }

    const [collection] = await ApiClient.getCollection({ id: String(id), view: 'with_categories' });

    if ('error' in collection) {
      return {
        notFound: true,
      };
    }

    const [category] = await ApiClient.getCategory({ id: String(cateId), view: 'with_image' });

    if ('error' in category) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        collection,
        category,
      },
      revalidate: RevalidateSettings.short,
    };
  };

export default Page;
